import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SecurityService } from '@app/services/security.service';
import { LoggerService } from '@app/core/logging/logger.service';

/**
 * Interceptor de responsabilidad única: inyecta el header `x-api-key`
 * en todas las peticiones dirigidas a los backends propios.
 *
 * El valor del header se cifra con CryptoJS AES (formato OpenSSL `Salted__`,
 * passphrase = `environment.keyInfoUser`) usando el helper centralizado
 * `SecurityService`. Backend valida ese ciphertext con `AES_DECRYPT`.
 *
 * Si el caller ya puso un `x-api-key` antes de que llegue el interceptor
 * (p. ej. `InfoUserService`), respetamos ese valor — significa que el
 * servicio aplicó su propio cifrado y no debemos sobrescribirlo.
 *
 * ⚠️ EXCEPCIÓN al estándar de seguridad del proyecto
 *   CLAUDE.md exige Web Crypto API + AES-GCM para código nuevo de cifrado.
 *   Aquí usamos CryptoJS AES-CBC (modo passphrase OpenSSL) por contrato
 *   explícito con backend: su middleware llama `decryptor.finalize()` sobre
 *   el header esperando el formato `Salted__` que SubtleCrypto.encrypt no
 *   produce. Migrar a AES-GCM requiere un cambio coordinado en backend
 *   (decrypt con AES-GCM y nonce explícito en el header). Tracking en la
 *   sección "Known Technical Debt" de CLAUDE.md.
 *
 * ⚠️ DEUDA DE SEGURIDAD adicional — la API key y la passphrase siguen
 *   expuestas al usuario final:
 *   1. Quedan en el bundle de producción (environment.ts se empaqueta).
 *   2. El header cifrado es visible en Network DevTools y reproducible
 *      con la passphrase del bundle.
 *
 * Mitigación definitiva: mover la API key a un backend proxy (BFF)
 * que la inyecte server-side. El frontend dejaría de enviar el header
 * y este interceptor se eliminaría.
 */
@Injectable()
export class ApiKeyInterceptor implements HttpInterceptor {
  private readonly security = inject(SecurityService);
  private readonly logger = inject(LoggerService);

  private readonly ownBaseUrls: string[] = [
    environment.graphqlApiUrl,
    environment.apiUrl,
    environment.apiStp,
    environment.storesApiUrl ?? '',
    environment.clientApiUrl ?? '',
    environment.notificationUrl,
  ].filter(Boolean);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // La fachada TPV (api/v1/tpv) se autentica SOLO con Bearer (sesión de banca);
    // no espera x-api-key (ver colección Postman). No inyectar el header ahí.
    if (request.url.includes('/api/v1/tpv/')) {
      return next.handle(request);
    }

    if (!this.isOwnBackend(request.url)) {
      return next.handle(request);
    }

    // Si el caller ya seteó el header, respetarlo (caso InfoUserService).
    // Evita que sobrescribamos un ciphertext válido con otro y causemos
    // mismatch de salt entre llamadas que dependen del mismo header.
    if (request.headers.has('x-api-key')) {
      return next.handle(request);
    }

    const encryptedKey = this.encryptApiKey();
    if (!encryptedKey) {
      // Si el cifrado falló no rompemos la request: backend devolverá 401/403
      // explícito en lugar de un error opaco de runtime aquí.
      return next.handle(request);
    }

    const cloned = request.clone({
      setHeaders: { 'x-api-key': encryptedKey },
    });
    return next.handle(cloned);
  }

  /**
   * Resuelve el valor de la API key a cifrar, con fallback explícito:
   *   1. `environment.xApiKey` — clave preferida (la que `InfoUserService`
   *      ya está usando con éxito en GuardarInfoUsuario).
   *   2. `environment.API_KEY` — legacy, mantener mientras coexistan ambos
   *      campos en `Environment.interface.ts`. Permite que un environment
   *      que solo defina uno de los dos siga funcionando.
   *
   * Cuando se elimine la duplicación en la interfaz (consolidar en un solo
   * campo), borrar el fallback y dejar solo `environment.xApiKey`.
   */
  private encryptApiKey(): string | null {
    const apiKey = environment.xApiKey || environment.API_KEY;
    if (!apiKey) {
      this.logger.warn('[ApiKeyInterceptor] xApiKey/API_KEY vacíos — no se inyecta header');
      return null;
    }
    try {
      return this.security.encrypt(apiKey);
    } catch (err) {
      this.logger.error('[ApiKeyInterceptor] fallo al cifrar x-api-key', err);
      return null;
    }
  }

  private isOwnBackend(url: string): boolean {
    return this.ownBaseUrls.some(base => url.startsWith(base));
  }
}
